/**
 * Shared field-editing state lifted above the fields panel and the
 * document overlay layer.
 *
 * Both surfaces edit the same fields and need to agree on:
 *   - which uuid → which dirty (unsaved) value
 *   - which uuid is currently selected (highlighted on the overlay)
 *   - which uuid is mid-save or last-errored, so we can flash a state
 *     marker without re-fetching the document
 *
 * The route owns the hook and threads the api down. The bulk-save path
 * stays in the fields panel (Save changes button); the overlay drives
 * single-field saves via {@link FieldStateMap.commitField}. The two share
 * the same dirty map so a value typed in the panel shows up immediately
 * on the overlay (and vice versa).
 */
import { useCallback, useMemo, useRef, useState } from 'react'

import type { Field, PatchSingleFieldBody } from '@/api/docfiller-types'

/**
 * Transient save state surfaced as a small marker on the overlay. The
 * field row itself uses the dirty buffer to decide "unsaved" — this enum
 * only tracks the saving / saved / error tail of the lifecycle.
 */
export type FieldSaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: number }
  | { kind: 'error'; message: string }

export interface ValidationResult {
  ok: boolean
  message: string | null
}

export interface FieldEditingApi {
  /** Map of uuid → current edited value (only entries the user touched). */
  dirty: Map<string, string | null>
  /** True iff at least one field has been edited and not yet saved. */
  hasChanges: boolean
  /** uuid of the field whose inline editor is currently open, or null. */
  selectedFieldId: string | null
  /** Per-field save status. Defaults to `{ kind: 'idle' }`. */
  statusFor: (uuid: string) => FieldSaveStatus
  /** Resolve the effective value: dirty override → committed value → null. */
  valueFor: (field: Pick<Field, 'uuid' | 'current_value'>) => string | null
  /** Run the registered validator (if any) for a candidate value. */
  validate: (field: Field, value: string | null) => ValidationResult
  /** Open the inline editor on a field. Null clears the selection. */
  selectField: (uuid: string | null) => void
  /** Set / clear the dirty value for a field. */
  setDirtyValue: (uuid: string, value: string | null) => void
  /** Drop the dirty entry for `uuid` (e.g. when the user cancels). */
  clearDirty: (uuid: string) => void
  /** Replace the whole dirty buffer (used by the panel's Save button). */
  setDirty: (
    update: (prev: Map<string, string | null>) => Map<string, string | null>,
  ) => void
  /** Set or clear the saving / saved / error indicator for one field. */
  setStatus: (uuid: string, status: FieldSaveStatus) => void
  /**
   * Remember a just-saved value until the document-detail cache catches up.
   * This prevents the UI from falling back to a stale server value during
   * the save-success -> refetch window.
   */
  markSavedValue: (uuid: string, value: string | null) => void
  /**
   * Commit a single field's edits via the supplied PATCH mutation,
   * flipping status to `saving` → `saved` (or `error`). Removes the
   * dirty entry on success so the overlay's "unsaved" badge disappears.
   *
   * The mutation function is passed in so this hook stays independent
   * of `react-query` mutation hooks (which can't be created inside an
   * already-mounted hook chain).
   */
  commitField: (
    field: Field,
    value: string | null,
    save: (body: PatchSingleFieldBody) => Promise<Field>,
  ) => Promise<void>
}

const IDLE_STATUS: FieldSaveStatus = { kind: 'idle' }

const EMPTY_VALIDATION: ValidationResult = { ok: true, message: null }

/**
 * Lightweight built-in validators keyed by `field.semantic_type`. The
 * router persists pretty much anything (the wire schema is `string | null`),
 * so this is purely UX guard-railing — we surface a red border + error
 * message under the input but the user can still hit save.
 */
function defaultValidate(field: Field, value: string | null): ValidationResult {
  if (value == null || value === '') return EMPTY_VALIDATION
  const trimmed = value.trim()
  if (!trimmed) return EMPTY_VALIDATION

  switch (field.semantic_type) {
    case 'email': {
      // Permissive but useful: must contain "@" and a domain dot.
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
      return ok ? EMPTY_VALIDATION : { ok: false, message: 'Enter a valid email address.' }
    }
    case 'phone': {
      const digits = trimmed.replace(/\D/g, '')
      if (digits.length < 7 || digits.length > 15) {
        return { ok: false, message: 'Enter a 7–15 digit phone number.' }
      }
      return EMPTY_VALIDATION
    }
    case 'zip': {
      const ok = /^\d{5}(?:-\d{4})?$/.test(trimmed)
      return ok ? EMPTY_VALIDATION : { ok: false, message: 'Use 5 or 9 digits (e.g. 94110 or 94110-1234).' }
    }
    case 'ssn': {
      const ok = /^\d{3}-?\d{2}-?\d{4}$/.test(trimmed)
      return ok
        ? EMPTY_VALIDATION
        : { ok: false, message: 'Enter as 9 digits, e.g. 123-45-6789.' }
    }
    case 'date': {
      if (field.kind !== 'date') return EMPTY_VALIDATION
      // <input type="date"> already gates the format, but legacy values may
      // be MM/DD/YYYY or DD-MM-YYYY. Accept any parseable date.
      const ms = Date.parse(trimmed)
      if (Number.isNaN(ms)) return { ok: false, message: 'Enter a valid date.' }
      return EMPTY_VALIDATION
    }
    default:
      return EMPTY_VALIDATION
  }
}

export function useFieldEditing(): FieldEditingApi {
  const [dirty, setDirtyState] = useState<Map<string, string | null>>(new Map())
  const [savedValues, setSavedValues] = useState<Map<string, string | null>>(new Map())
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [statusMap, setStatusMap] = useState<Map<string, FieldSaveStatus>>(new Map())

  // Hold the "saved" auto-clear timers so a fast double-save doesn't leak
  // a setTimeout that races against the next status flip.
  const savedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const savedValueTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const hasChanges = dirty.size > 0

  const setDirty = useCallback(
    (
      update: (prev: Map<string, string | null>) => Map<string, string | null>,
    ) => {
      setDirtyState((prev) => update(prev))
    },
    [],
  )

  const setDirtyValue = useCallback((uuid: string, value: string | null) => {
    const valueTimer = savedValueTimersRef.current.get(uuid)
    if (valueTimer) {
      clearTimeout(valueTimer)
      savedValueTimersRef.current.delete(uuid)
    }
    setSavedValues((prev) => {
      if (!prev.has(uuid)) return prev
      const next = new Map(prev)
      next.delete(uuid)
      return next
    })
    setDirtyState((prev) => {
      const next = new Map(prev)
      next.set(uuid, value)
      return next
    })
  }, [])

  const clearDirty = useCallback((uuid: string) => {
    setDirtyState((prev) => {
      if (!prev.has(uuid)) return prev
      const next = new Map(prev)
      next.delete(uuid)
      return next
    })
  }, [])

  const markSavedValue = useCallback((uuid: string, value: string | null) => {
    setSavedValues((prev) => {
      const next = new Map(prev)
      next.set(uuid, value)
      return next
    })
    const timers = savedValueTimersRef.current
    const existing = timers.get(uuid)
    if (existing) {
      clearTimeout(existing)
      timers.delete(uuid)
    }
    const handle = setTimeout(() => {
      timers.delete(uuid)
      setSavedValues((prev) => {
        if (!prev.has(uuid)) return prev
        const next = new Map(prev)
        next.delete(uuid)
        return next
      })
    }, 8000)
    timers.set(uuid, handle)
  }, [])

  const setStatus = useCallback((uuid: string, status: FieldSaveStatus) => {
    setStatusMap((prev) => {
      const next = new Map(prev)
      if (status.kind === 'idle') {
        next.delete(uuid)
      } else {
        next.set(uuid, status)
      }
      return next
    })
    // Auto-clear "saved" markers after a short pause so the green checkmark
    // doesn't get stuck on the overlay.
    const timers = savedTimersRef.current
    const existing = timers.get(uuid)
    if (existing) {
      clearTimeout(existing)
      timers.delete(uuid)
    }
    if (status.kind === 'saved') {
      const handle = setTimeout(() => {
        timers.delete(uuid)
        setStatusMap((prev) => {
          if (!prev.has(uuid)) return prev
          const next = new Map(prev)
          next.delete(uuid)
          return next
        })
      }, 1800)
      timers.set(uuid, handle)
    }
  }, [])

  const statusFor = useCallback(
    (uuid: string): FieldSaveStatus => statusMap.get(uuid) ?? IDLE_STATUS,
    [statusMap],
  )

  const valueFor = useCallback(
    (field: Pick<Field, 'uuid' | 'current_value'>): string | null => {
      if (dirty.has(field.uuid)) return dirty.get(field.uuid) ?? null
      if (savedValues.has(field.uuid)) {
        const saved = savedValues.get(field.uuid) ?? null
        if ((field.current_value ?? null) !== saved) return saved
      }
      return field.current_value ?? null
    },
    [dirty, savedValues],
  )

  const validate = useCallback(
    (field: Field, value: string | null) => defaultValidate(field, value),
    [],
  )

  const commitField = useCallback<FieldEditingApi['commitField']>(
    async (field, value, save) => {
      const validation = defaultValidate(field, value)
      if (!validation.ok) {
        setStatus(field.uuid, {
          kind: 'error',
          message: validation.message ?? 'Invalid value.',
        })
        return
      }
      setStatus(field.uuid, { kind: 'saving' })
      try {
        await save({ current_value: value, accepted_by: 'user' })
        markSavedValue(field.uuid, value)
        clearDirty(field.uuid)
        setStatus(field.uuid, { kind: 'saved', at: Date.now() })
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Save failed. Please retry.'
        setStatus(field.uuid, { kind: 'error', message })
        // Leave the dirty entry in place so the user's edit isn't lost.
      }
    },
    [clearDirty, markSavedValue, setStatus],
  )

  return useMemo<FieldEditingApi>(
    () => ({
      dirty,
      hasChanges,
      selectedFieldId,
      statusFor,
      valueFor,
      validate,
      selectField: setSelectedFieldId,
      setDirtyValue,
      clearDirty,
      setDirty,
      setStatus,
      markSavedValue,
      commitField,
    }),
    [
      dirty,
      hasChanges,
      selectedFieldId,
      statusFor,
      valueFor,
      validate,
      setDirtyValue,
      clearDirty,
      setDirty,
      setStatus,
      markSavedValue,
      commitField,
    ],
  )
}
